import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { SearchResultResponse, SearchState } from 'src/app/interfaces/search.interface';
import {
  FullUser,
  SearchResult,
  SuggestedUser,
  SuggestedUsers,
} from '../user-relation-requests/relation-request.interface';
import { SearchStrategyTypes } from './search.enum';
import { SearchService } from './search.service';

@Injectable()
export class UsersSearchService {
  private searchService = inject(SearchService);

  public readonly isLoading$: Observable<boolean> = this.searchService.isLoading$;

  private listChanges$: BehaviorSubject<SuggestedUsers> = new BehaviorSubject({ items: [], totalCount: 0 });

  public constructor() {
    this.searchService.searchResultsChanges
      .pipe(
        takeUntilDestroyed(),
        map((response: SearchResultResponse) => ({
          items: this.parseSearchUsers(response),
          totalCount: response.total_matches_count,
        })),
      )
      .subscribe((result: SuggestedUsers) => {
        this.listChanges$.next(result);
      });
  }

  public get suggestionsList$(): Observable<SuggestedUsers> {
    return this.listChanges$.asObservable();
  }

  public suggestUsers(state: SearchState): void {
    const { pagination, sortParams, params } = state;

    if (!params.queryText) {
      return;
    }

    this.searchService.setStrategy(SearchStrategyTypes.INVITE_NAMES);
    this.searchService.getSearchResults({
      applyLocationAttrs: true,
      state: {
        pagination,
        sortParams,
        params: { queryText: params.queryText.toLowerCase().trim(), itemKind: params.itemKind },
      },
    });
  }

  private parseSearchUsers(response: SearchResultResponse): SuggestedUser[] {
    return response.search_results
      .map((result: SearchResult) => result.item as FullUser)
      .map((user: FullUser) => ({
        user: {
          user_id: user.user_id,
          displayed_name: user.displayed_name,
          is_admin: user.is_admin,
          account_status: user.account_status,
          image_url: user.image_url,
          reaction: {
            blocked: user.reaction?.blocked,
          },
        },
        doctor: user.doctor,
        patient: user.patient,
        connectionDetails: user.reaction?.connection_details,
        doctorLinkStatus: user.reaction?.doctor_link_status,
        patientLinkStatus: user.reaction?.patient_link_status,
      }));
  }
}
